import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  initialsFromName,
} from '@mlm/ui';
import type { ShowcaseRank } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { useRankAchievers } from '../../lib/hooks/use-ranks';
import { RankIcon } from './rank-icon';
import { rankDisplayName } from './rank-display';

/**
 * Who holds a rank: icon, name, description, then a paginated list of masked
 * achiever names (the API masks them; this never sees a full name).
 */
export function RankAchieversDialog({
  rank,
  onOpenChange,
}: {
  rank: ShowcaseRank | null;
  onOpenChange: (open: boolean) => void;
}) {
  const q = useRankAchievers(rank?.code ?? null);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Dialog open={!!rank} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        {rank && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <RankIcon
                  key={rank.code}
                  iconUrl={rank.iconUrl}
                  index={rank.index}
                  className="h-12 w-12"
                />
                <div className="min-w-0 text-left">
                  <DialogTitle className="capitalize">{rankDisplayName(rank)}</DialogTitle>
                  <DialogDescription>
                    {rank.achieverCount} {rank.achieverCount === 1 ? 'achiever' : 'achievers'}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            {rank.description && (
              <p className="whitespace-pre-line text-sm text-muted-foreground">
                {rank.description}
              </p>
            )}
            <div className="mt-2">
              {q.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No one has reached this rank yet.
                </p>
              ) : (
                <ul className="divide-y">
                  {items.map((a, i) => (
                    <li
                      key={`${a.name}-${a.achievedAt}-${i}`}
                      className="flex items-center gap-3 py-2"
                    >
                      <Avatar src={a.avatarUrl} initials={initialsFromName(a.name)} />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">{a.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {fmtDate(a.achievedAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {q.hasNextPage && (
                <div className="mt-3 flex justify-center">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={q.isFetchingNextPage}
                    onClick={() => void q.fetchNextPage()}
                  >
                    {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
